import React, { useState, useEffect } from 'react';
import { Building2, TrendingUp, TrendingDown, Users } from 'lucide-react';
import { Brokerage, Recommendation, FilterOptions, SearchFilters } from '../types';
import { apiService } from '../services/api';
import { RecommendationCard } from '../components/RecommendationCard';
import { LoadingState } from '../components/LoadingSpinner';
import { ErrorState } from '../components/ErrorBoundary';
import { useOutletContext } from 'react-router-dom';


interface OutletContextType {
  filters: FilterOptions;
  searchQuery: string;
}

export const BrokeragesPage: React.FC = () => {
  const { filters, searchQuery } = useOutletContext<OutletContextType>();
  const [brokerages, setBrokerages] = useState<Brokerage[]>([]);
  const [recommendations, setRecommendations] = useState<Recommendation[]>([]);
  const [selectedBrokerage, setSelectedBrokerage] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchBrokerages();
  }, []);

  useEffect(() => {
    if (selectedBrokerage) {
      fetchBrokerageRecommendations(selectedBrokerage, { query: searchQuery, filters });
    }
  }, [selectedBrokerage, filters, searchQuery]);

  const fetchBrokerages = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await apiService.getBrokerages();
      if (response.success) {
        setBrokerages(response.data);
        // Select first brokerage by default
        if (response.data.length > 0) {
          setSelectedBrokerage(response.data[0].slug);
        }
      } else {
        setError('Failed to fetch brokerages');
      }
    } catch (err) {
      console.error('Error fetching brokerages:', err);
      setError('Failed to connect to server.');
    } finally {
      setLoading(false);
    }
  };

  const fetchBrokerageRecommendations = async (brokerageSlug: string, searchFilters: SearchFilters) => {
    try {
      const response = await apiService.getBrokerageRecommendations(brokerageSlug, searchFilters);
      if (response.success) {
        // Map API response to match frontend expectations
        const mappedData = response.data.map((rec: any) => ({
          ...rec,
          stock: rec.company ? {
            id: rec.company.id,
            symbol: rec.company.seo_name || rec.company.name,
            name: rec.company.name,
            sector: rec.company.sector || 'Diversified',
            current_price: rec.current_price
          } : null,
          brokerage: rec.organization ? {
            id: rec.organization.id,
            name: rec.organization.name,
            slug: rec.organization.seo_name
          } : null,
          recommendation: rec.recommendation_type,
          potentialUpside: rec.upside_potential,
          potential_value: rec.upside_potential,
          targetPrice: rec.target_price,
          target_price: rec.target_price,
          stock_score: rec.stock_score,
          stock_score_data: rec.stock_score_data
        }));
        setRecommendations(mappedData);
      } else {
        setError('Failed to fetch recommendations');
      }
    } catch (err) {
      console.error('Error fetching brokerage recommendations:', err);
      setError('Failed to fetch recommendations');
    }
  };

  const getBrokerageStats = (brokerageSlug: string) => {
    const brokerageRecos = recommendations.filter(r => r.brokerage?.slug === brokerageSlug);
    return {
      total: brokerageRecos.length,
      buy: brokerageRecos.filter(r => r.recommendation === 'Buy').length,
      sell: brokerageRecos.filter(r => r.recommendation === 'Sell').length,
      hold: brokerageRecos.filter(r => r.recommendation === 'Hold').length,
      avgUpside: brokerageRecos.length > 0 
        ? brokerageRecos.reduce((sum, r) => sum + (r.potentialUpside || 0), 0) / brokerageRecos.length 
        : 0
    };
  };

  const handleRecommendationClick = (recommendation: Recommendation) => {
    const symbol = recommendation.company?.seo_name || recommendation.stock?.symbol;
    window.location.href = `/stock/${symbol}`;
  };

  if (loading) {
    return <LoadingState message="Loading brokerages..." />;
  }

  if (error && brokerages.length === 0) {
    return <ErrorState message={error} onRetry={fetchBrokerages} />;
  }

  const selectedBrokerageData = brokerages.find(b => b.slug === selectedBrokerage);
  const stats = selectedBrokerage ? getBrokerageStats(selectedBrokerage) : null;

  return (
    <div className="min-h-screen text-white">
      <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-8">
        <h2 className="text-3xl font-bold text-white mb-2">
          Brokerage Recommendations
        </h2>
        <p className="text-gray-400">
          Explore stock recommendations organized by brokerage firms
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Brokerages List */}
        <div className="lg:col-span-1">
          <div className="bg-obsidian rounded-lg shadow-sm border border-white/20 shadow shadow-white/50 sticky top-20 p-4">
            <h3 className="text-lg font-semibold text-white mb-4">Brokerages</h3>
            <div className="space-y-2">
              {brokerages.map((brokerage) => {
                return (
                  <button
                    key={brokerage.id}
                    onClick={() => setSelectedBrokerage(brokerage.slug)}
                    className={`w-full text-left p-3 rounded-xl transition-all duration-200 bg-obsidian text-white ${
                      selectedBrokerage === brokerage.slug
                        ? 'bg-white text-black shadow-md'
                        : 'hover:bg-gray-50 border text-black border-gray-200'
                    }`}
                  >
                    <div className="flex items-center mb-1">
                      <Building2 className="h-4 w-4 mr-2" />
                      <span className="font-semibold text-sm">{brokerage.name}</span>
                    </div>
                    <div className={`text-xs ${
                      selectedBrokerage === brokerage.slug ? 'text-blue-100' : 'text-gray-600'
                    }`}>
                      {(brokerage as any).recommendations_count || brokerage.total_count || 0} recommendations
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* Selected Brokerage Content */}
        <div className="lg:col-span-3">
          {selectedBrokerageData && stats && (
            <>
              {/* Brokerage Header */}
              <div className="bg-gradient-to-br from-white via-blue-50/30 to-indigo-50/20 rounded-2xl shadow-lg border border-gray-200 p-8 mb-6">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900">{selectedBrokerageData.name}</h3>
                    {selectedBrokerageData.website && (
                      <a
                        href={selectedBrokerageData.website}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:text-blue-800 text-sm font-medium inline-flex items-center gap-1 mt-1"
                      >
                        Visit Website →
                      </a>
                    )}
                  </div>
                  <div className="text-right bg-white/60 backdrop-blur-sm rounded-xl p-4 border border-gray-200">
                    <div className="text-3xl font-bold text-gray-900">{stats.total}</div>
                    <div className="text-sm text-gray-600">Total Recommendations</div>
                  </div>
                </div>

                {/* Stats Grid */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="text-center p-4 bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl border border-green-200">
                    <div className="flex items-center justify-center mb-2">
                      <TrendingUp className="h-5 w-5 text-green-600" />
                    </div>
                    <div className="text-2xl font-bold text-green-600">{stats.buy}</div>
                    <div className="text-xs text-green-700 font-medium">Buy Calls</div>
                  </div>
                  <div className="text-center p-4 bg-gradient-to-br from-red-50 to-rose-50 rounded-xl border border-red-200">
                    <div className="flex items-center justify-center mb-2">
                      <TrendingDown className="h-5 w-5 text-red-600" />
                    </div>
                    <div className="text-2xl font-bold text-red-600">{stats.sell}</div>
                    <div className="text-xs text-red-700 font-medium">Sell Calls</div>
                  </div>
                  <div className="text-center p-4 bg-gradient-to-br from-yellow-50 to-amber-50 rounded-xl border border-yellow-200">
                    <div className="flex items-center justify-center mb-2">
                      <Users className="h-5 w-5 text-yellow-600" />
                    </div>
                    <div className="text-2xl font-bold text-yellow-600">{stats.hold}</div>
                    <div className="text-xs text-yellow-700 font-medium">Hold Calls</div>
                  </div>
                  <div className="text-center p-4 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl border border-blue-200">
                    <div className="flex items-center justify-center mb-2">
                      <TrendingUp className="h-5 w-5 text-blue-600" />
                    </div>
                    <div className="text-2xl font-bold text-blue-600">
                      {stats.avgUpside.toFixed(1)}%
                    </div>
                    <div className="text-xs text-blue-700 font-medium">Avg Upside</div>
                  </div>
                </div>
              </div>

              {/* Recommendations Grid */}
              {recommendations.length > 0 ? (
                <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
                  {recommendations.map((recommendation) => (
                    <RecommendationCard
                      key={recommendation.id}
                      recommendation={recommendation}
                      onClick={handleRecommendationClick}
                    />
                  ))}
                </div>
              ) : (
                <div className="bg-white rounded-2xl shadow-lg border border-gray-200 text-center py-16">
                  <Building2 className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600 text-lg">No recommendations found for this brokerage</p>
                </div>
              )}
            </>
          )}
        </div>
      </div>
      </div>
    </div>
  );
};